import { DEFAULT_DIALOG_CONFIG } from '@angular/cdk/dialog';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
    withXhr,
} from '@angular/common/http';
import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
    provideRouter,
    TitleStrategy,
    withComponentInputBinding,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
    withRouterConfig,
} from '@angular/router';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { KEEP, RemoveTypenameFromVariablesLink } from '@apollo/client/link/remove-typename';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { OKTA_CONFIG, OktaAuthModule } from '@okta/okta-angular';
import OktaAuth, { OktaAuthOptions, StorageType } from '@okta/okta-auth-js';
import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { provideApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { provideToastr } from 'ngx-toastr';
import { appRoutes } from './app.routes';
import { namespaceTypePolicies } from './shared/components/aperture-credentials-management/generated/namespace-type-policies';
import { AppTitleStrategy } from './shared/services/nav/app-title.strategy';
import { BackNavService } from './shared/services/nav/back-nav';
import { LcBsToastComponent } from './shared/toasts/lcbs-toast';
import { AuthExpirationService } from './shared/utils/auth/auth.expiration.service';
import { AuthInterceptor } from './shared/utils/auth/auth.interceptor';
import { WINDOW_PROVIDERS } from './shared/utils/browser/window.provider';
import { APP_CONFIG, AppConfig } from './shared/utils/config/app-config';

/**
 * Services listed here are `providedIn: 'root'` but subscribe to app-wide
 * events (e.g. router NavigationEnd) in their constructors. Angular lazily
 * creates root-provided services on first injection, so if nothing injects
 * them early enough they miss events that fire before they exist.
 *
 * Calling `inject()` inside `provideAppInitializer` forces creation during
 * bootstrap -- before the first router navigation.
 */
function eagerlyInstantiateServices(): void {
    inject(BackNavService);
    inject(AuthExpirationService);
}

/**
 * Resolve any unrecognized mat-icon `svgIcon` name to the per-file SVG asset
 * emitted by `prebuild:copy-icons`. The browser fetches each file lazily on
 * first reference and HttpClient caches it for the page lifetime. No per-icon
 * `addSvgIcon` call site is needed because the URL is mechanically derived
 * from the name.
 */
function registerMaterialSymbolsResolver(): void {
    const registry = inject(MatIconRegistry);
    const sanitizer = inject(DomSanitizer);
    // The substring `material-symbols-rounded` in the URLs below is the asset
    // directory name (matching what scripts/copy-material-symbol-icons.mjs
    // emits to dist), not the legacy CSS class. The ESLint rule cannot tell
    // the difference, so we suppress on the literal lines.
    //
    // Two namespaces are supported:
    //   - default (no namespace): filled variant of each glyph, preserving the
    //     pre-migration FILL=1 woff2 look. Used as svgIcon="<name>".
    //   - 'outlined': outlined variant for icons that need a lighter feel.
    //     Used as svgIcon="outlined:<name>". Sourced from the parallel
    //     `outlined/` subdirectory the build script emits.
    //   (Examples avoid the literal mat-icon syntax to prevent the build
    //    script's icon-name extractor from harvesting these comment strings
    //    as real icon usages -- it walks .ts files and cannot distinguish
    //    code from comments.)
    registry.addSvgIconResolver((name, namespace) => {
        const subdir = namespace === 'outlined' ? 'outlined/' : '';
        return sanitizer.bypassSecurityTrustResourceUrl(
            // eslint-disable-next-line no-restricted-syntax
            `/assets/icons/material-symbols-rounded/${subdir}${name}.svg`
        );
    });
}

ModuleRegistry.registerModules([AllCommunityModule]);
/**
 * Global ag-grid defaults applied to every grid in the app. `enableCellTextSelection`
 * lets users select and copy cell text; `ensureDomOrder` keeps the DOM row order in
 * sync with the visual order so selections span rows correctly. Per-grid `gridOptions`
 * still merge on top of these defaults.
 */
provideGlobalGridOptions({
    enableCellTextSelection: true,
    ensureDomOrder: true,
});

/**
 * Base Bootstrap modal panel classes applied to every CDK Dialog overlay via
 * `DEFAULT_DIALOG_CONFIG`. Exported so that individual `dialog.open()` callers
 * can append size variants (`modal-xl`, `modal-lg`) without losing the base
 * classes -- CDK's `DialogConfig.panelClass` *replaces* the default when set
 * on a specific call, so callers must spread this list explicitly.
 */
export const DEFAULT_PANEL_CLASSES: string[] = [
    'modal',
    'modal-dialog',
    'modal-dialog-scrollable',
    'fade',
    'show',
    'align-self-baseline',
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            appRoutes,
            withEnabledBlockingInitialNavigation(),
            withComponentInputBinding(),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withRouterConfig({
                paramsInheritanceStrategy: 'always',
            })
        ),
        provideAppInitializer(eagerlyInstantiateServices),
        provideAppInitializer(registerMaterialSymbolsResolver),
        { provide: TitleStrategy, useClass: AppTitleStrategy },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideStore(
            {},
            {
                metaReducers: [],
                runtimeChecks: {
                    strictActionImmutability: true,
                    strictStateImmutability: true,
                },
            }
        ),
        provideEffects(),
        importProvidersFrom(OktaAuthModule),
        importProvidersFrom(
            LoggerModule.forRoot({
                level: isDevMode() ? NgxLoggerLevel.DEBUG : NgxLoggerLevel.ERROR,
            })
        ),
        WINDOW_PROVIDERS,
        {
            provide: OKTA_CONFIG,
            deps: [APP_CONFIG],
            useFactory: (config: AppConfig) => {
                // Defaults to memory-only token storage to prevent credential leakage.
                // Overridable via runtime config for E2E tests that need localStorage persistence.
                const tokenStorageType = (config.tokenStorageType ?? 'memory') as StorageType;
                const options: OktaAuthOptions = {
                    clientId: config.clientId,
                    issuer: config.issuer,
                    redirectUri: 'login/callback',
                    scopes: config.authScopes,
                    pkce: true,
                    tokenManager: {
                        storage: tokenStorageType,
                    },
                    storageManager: {
                        token: {
                            storageType: tokenStorageType,
                            storageTypes: [],
                        },
                    },
                    postLogoutRedirectUri: `${globalThis.location.origin}/logout/callback`,
                };
                const oktaAuth = new OktaAuth(options);
                return { oktaAuth };
            },
            multi: false,
        },
        provideToastr({
            timeOut: 5_000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: true,
            closeButton: true,
            toastComponent: LcBsToastComponent,
        }),
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        provideApollo(
            () => {
                const httpLink = inject(HttpLink).create({ uri: '/api/graphql/query' });
                const removeTypenameLink = new RemoveTypenameFromVariablesLink({
                    except: {
                        JSON: KEEP,
                    },
                });
                // note: We have not used an ErrorLink for user feedback because the user feedback
                // changes on a case by case basis.
                // We used an ErrorLink in PrescriptiveAuthority and had to define the error message for each
                // case anyways and had to specify when errors should be ignored.
                return {
                    link: ApolloLink.from([removeTypenameLink, httpLink]),
                    cache: new InMemoryCache({
                        typePolicies: namespaceTypePolicies,
                    }),
                    defaultOptions: {
                        watchQuery: {
                            fetchPolicy: 'cache-and-network',
                        },
                        query: {
                            fetchPolicy: 'cache-first',
                        },
                    },
                };
            },
            {
                // https://the-guild.dev/graphql/apollo-angular/docs/data/mutations#loading-state
                useMutationLoading: true,
            }
        ),
        {
            provide: DEFAULT_DIALOG_CONFIG,
            useValue: {
                hasBackdrop: true,
                backdropClass: ['modal-backdrop', 'fade', 'show'],
                panelClass: DEFAULT_PANEL_CLASSES,
            },
        },
    ],
};
